import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, CircularProgress } from '@mui/material';
import axios from '../../../interceptors/axios';

interface EditorProps {
  /** When provided, component acts as an embedded modal (no routing) */
  fileId?: number;
  onClose?: () => void;
}

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:3002/api').replace(/\/api$/, '');

interface MediaFile {
  id: number;
  original_name: string;
  mime_type: string;
  size_bytes: number;
  width: number | null;
  height: number | null;
  created_at: string;
}

const fmtSize = (b: number) => {
  if (b >= 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${Math.round(b / 1024)} KB`;
};

const glassSx = {
  background: 'var(--glass-bg)',
  backdropFilter: 'blur(20px)',
  border: '1px solid var(--glass-border)',
};

// ── Small reusable components ──────────────────────────────────────────────

const SectionHeader: React.FC<{
  icon: React.ReactNode;
  iconColor: string;
  title: string;
  badge?: string;
  open: boolean;
  onToggle: () => void;
}> = ({ icon, iconColor, title, badge, open, onToggle }) => (
  <Box
    onClick={onToggle}
    sx={{
      display: 'flex', alignItems: 'center', gap: '8px',
      px: '14px', py: '8px', cursor: 'pointer', userSelect: 'none',
      '&:hover': { background: 'rgba(255,255,255,0.04)' },
    }}
  >
    <Box sx={{ width: 26, height: 26, borderRadius: '7px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: iconColor, flexShrink: 0 }}>
      {icon}
    </Box>
    <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', flex: 1 }}>{title}</Typography>
    {badge && (
      <Typography sx={{ fontSize: 10, fontWeight: 600, color: 'var(--accent)', background: 'rgba(91,141,238,.15)', px: '6px', py: '2px', borderRadius: '20px' }}>{badge}</Typography>
    )}
    <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .2s' }}>›</Typography>
  </Box>
);

const SliderControl: React.FC<{
  label: string;
  value: number;
  min: number; max: number; step?: number;
  displayValue?: string;
  onChange: (v: number) => void;
}> = ({ label, value, min, max, step = 1, displayValue, onChange }) => (
  <Box sx={{ mb: '10px' }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: '5px' }}>
      <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)', fontWeight: 500 }}>{label}</Typography>
      <Typography sx={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
        {displayValue ?? (value > 0 && min < 0 ? `+${value}` : `${value}`)}
      </Typography>
    </Box>
    <input
      type="range" min={min} max={max} step={step} value={value}
      onChange={e => onChange(Number(e.target.value))}
      style={{ width: '100%', height: 4, accentColor: 'var(--accent)', cursor: 'pointer' }}
    />
  </Box>
);

const Toggle: React.FC<{ label: string; value: boolean; onChange: (v: boolean) => void }> = ({ label, value, onChange }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: '5px' }}>
    <Typography sx={{ fontSize: 12, color: 'var(--text-secondary)' }}>{label}</Typography>
    <Box
      onClick={() => onChange(!value)}
      sx={{ width: 32, height: 18, borderRadius: '9px', background: value ? 'var(--accent)' : 'var(--glass-border)', position: 'relative', cursor: 'pointer', transition: 'background .2s', flexShrink: 0 }}
    >
      <Box sx={{ position: 'absolute', top: '2px', left: value ? '16px' : '2px', width: 14, height: 14, borderRadius: '50%', background: '#fff', transition: 'left .2s', boxShadow: '0 1px 4px rgba(0,0,0,.3)' }} />
    </Box>
  </Box>
);

// ── Main Component ─────────────────────────────────────────────────────────

const ImageEditorPage: React.FC<EditorProps> = ({ fileId: propFileId, onClose }) => {
  const { id: routeId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const id = propFileId != null ? String(propFileId) : routeId;
  const handleClose = onClose ?? (() => navigate('/media'));
  const [saveError, setSaveError] = useState<string | null>(null);

  const [file, setFile] = useState<MediaFile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);
  const [zoom, setZoom] = useState(1);

  // Section open state
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    resize: true, quality: true, rotate: true, crop: false, color: false, watermark: false,
  });
  const toggleSection = (key: string) => setOpenSections(p => ({ ...p, [key]: !p[key] }));

  // Edit state
  const [resizeEnabled, setResizeEnabled] = useState(false);
  const [resizeW, setResizeW] = useState(0);
  const [resizeH, setResizeH] = useState(0);
  const [aspectLocked, setAspectLocked] = useState(true);
  const [upscale, setUpscale] = useState(false);
  const [quality, setQuality] = useState(82);
  const [format, setFormat] = useState<'original' | 'jpeg' | 'webp' | 'png' | 'avif'>('webp');
  const [stripExif, setStripExif] = useState(true);
  const [progressive, setProgressive] = useState(true);
  const [rotateAngle, setRotateAngle] = useState(0);
  const [flip, setFlip] = useState(false);
  const [flop, setFlop] = useState(false);
  const [brightness, setBrightness] = useState(0);
  const [contrast, setContrast] = useState(0);
  const [saturation, setSaturation] = useState(0);
  const [sharpness, setSharpness] = useState(0);

  const originalAspect = useRef(1);

  useEffect(() => {
    if (!id) return;
    axios.get('/media/files').then(({ data }) => {
      const f = (data as MediaFile[]).find(x => x.id === +id);
      if (f) {
        setFile(f);
        setResizeW(f.width ?? 1920);
        setResizeH(f.height ?? 1080);
        originalAspect.current = f.width && f.height ? f.width / f.height : 16 / 9;
      }
    }).finally(() => setLoading(false));
  }, [id]);

  const handleResizeW = (v: number) => {
    setResizeW(v);
    if (aspectLocked && v > 0) setResizeH(Math.round(v / originalAspect.current));
  };
  const handleResizeH = (v: number) => {
    setResizeH(v);
    if (aspectLocked && v > 0) setResizeW(Math.round(v * originalAspect.current));
  };

  const rotate90 = (dir: 1 | -1) => setRotateAngle(prev => ((prev + dir * 90) % 360 + 360) % 360);

  const isModified = rotateAngle !== 0 || flip || flop || resizeEnabled || brightness !== 0 || contrast !== 0 || saturation !== 0 || sharpness !== 0;

  const reset = () => {
    setResizeEnabled(false);
    setResizeW(file?.width ?? 1920);
    setResizeH(file?.height ?? 1080);
    setAspectLocked(true);
    setQuality(82);
    setFormat('webp');
    setStripExif(true);
    setProgressive(true);
    setRotateAngle(0);
    setFlip(false);
    setFlop(false);
    setBrightness(0);
    setContrast(0);
    setSaturation(0);
    setSharpness(0);
  };

  const buildParams = (saveCopy: boolean) => ({
    resize: resizeEnabled ? { width: resizeW, height: resizeH, upscale } : undefined,
    quality,
    format,
    rotate: rotateAngle !== 0 ? rotateAngle : undefined,
    flip: flip || undefined,
    flop: flop || undefined,
    brightness: brightness !== 0 ? brightness : undefined,
    contrast: contrast !== 0 ? contrast : undefined,
    saturation: saturation !== 0 ? saturation : undefined,
    sharpen: sharpness > 0 ? sharpness : undefined,
    stripMetadata: stripExif,
    saveCopy,
  });

  const save = useCallback(async (saveCopy: boolean) => {
    if (!id) return;
    setSaving(true);
    setSaveError(null);
    try {
      await axios.post(`/media/files/${id}/process`, buildParams(saveCopy));
      handleClose();
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || 'Save failed';
      setSaveError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSaving(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, resizeEnabled, resizeW, resizeH, upscale, quality, format, stripExif, progressive, rotateAngle, flip, flop, brightness, contrast, saturation, sharpness]);

  // CSS filter for live preview
  const previewFilter = [
    brightness !== 0 ? `brightness(${1 + brightness / 100})` : '',
    contrast !== 0 ? `contrast(${1 + contrast / 100})` : '',
    saturation !== 0 ? `saturate(${1 + saturation / 100})` : '',
    sharpness > 0 ? `blur(0px)` : '',
  ].filter(Boolean).join(' ') || 'none';

  const previewTransform = [
    `rotate(${rotateAngle}deg)`,
    flop ? 'scaleX(-1)' : '',
    flip ? 'scaleY(-1)' : '',
  ].filter(Boolean).join(' ');

  const ext = file?.original_name.split('.').pop()?.toUpperCase() ?? '';

  // Estimated output size (rough calculation)
  const estSize = (() => {
    if (!file) return null;
    const pixelRatio = resizeEnabled ? (resizeW * resizeH) / ((file.width ?? 1) * (file.height ?? 1)) : 1;
    const qualityRatio = quality / 100;
    const formatBonus = format === 'webp' ? 0.5 : format === 'avif' ? 0.4 : format === 'png' ? 1.3 : 1;
    return Math.round(file.size_bytes * pixelRatio * qualityRatio * formatBonus);
  })();

  const sizeDiff = estSize && file ? Math.round((1 - estSize / file.size_bytes) * 100) : 0;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <CircularProgress size={32} sx={{ color: 'var(--accent)' }} />
      </Box>
    );
  }

  if (!file) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <Typography sx={{ color: 'var(--text-disabled)' }}>File not found</Typography>
      </Box>
    );
  }

  const imageUrl = `${API_BASE}/api/media/file/${file.id}`;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', fontFamily: 'var(--font-family, DM Sans, sans-serif)' }}>

      {/* ── Top Bar ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', px: '16px', height: 50, background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid var(--glass-border)', flexShrink: 0 }}>
        <Box
          onClick={() => handleClose()}
          sx={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--text-disabled)', fontSize: 13, cursor: 'pointer', px: '8px', py: '4px', borderRadius: '7px', '&:hover': { background: 'rgba(255,255,255,0.07)', color: 'var(--text-primary)' } }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
          Media
        </Box>
        <Box sx={{ width: 1, height: 16, background: 'var(--glass-border)', flexShrink: 0 }} />
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {file.original_name}
        </Typography>
        <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--glass-border)', px: '8px', py: '2px', borderRadius: '20px' }}>
          {file.width && file.height ? `${file.width} × ${file.height}` : ''} · {ext} · {fmtSize(file.size_bytes)}
        </Typography>

        <Box sx={{ flex: 1 }} />

        <Box
          onClick={() => setShowOriginal(p => !p)}
          sx={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: 12, fontWeight: 500, color: showOriginal ? 'var(--accent)' : 'var(--text-disabled)', background: showOriginal ? 'rgba(91,141,238,.1)' : 'transparent', border: '1px solid', borderColor: showOriginal ? 'rgba(91,141,238,.3)' : 'var(--glass-border)', px: '10px', py: '5px', borderRadius: '7px', cursor: 'pointer', transition: 'all .15s' }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
          Before / After
        </Box>

        {isModified && (
          <Box
            onClick={reset}
            sx={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: 12, color: '#f87171', background: 'rgba(248,113,113,.08)', border: '1px solid rgba(248,113,113,.2)', px: '10px', py: '5px', borderRadius: '7px', cursor: 'pointer', '&:hover': { background: 'rgba(248,113,113,.15)' } }}
          >
            Reset all
          </Box>
        )}

        <Box
          onClick={() => !saving && save(true)}
          sx={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: 12, fontWeight: 500, color: 'var(--text-primary)', background: 'rgba(255,255,255,0.07)', border: '1px solid var(--glass-border)', px: '12px', py: '6px', borderRadius: '7px', cursor: saving ? 'default' : 'pointer', opacity: saving ? .6 : 1, '&:hover': { background: 'rgba(255,255,255,0.11)' } }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/></svg>
          Save copy
        </Box>

        <Box
          onClick={() => !saving && save(false)}
          sx={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: 12, fontWeight: 600, color: '#fff', background: '#3a9e6e', px: '12px', py: '6px', borderRadius: '7px', cursor: saving ? 'default' : 'pointer', opacity: saving ? .6 : 1, '&:hover': { background: '#34896a' } }}
        >
          {saving ? <CircularProgress size={12} sx={{ color: '#fff' }} /> : (
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
          )}
          Apply & replace
        </Box>
      </Box>

      {/* ── Save error ── */}
      {saveError && (
        <Box sx={{ px: '16px', py: '7px', background: 'rgba(248,113,113,.12)', borderBottom: '1px solid rgba(248,113,113,.25)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <Typography sx={{ fontSize: 12, color: '#f87171' }}>{saveError}</Typography>
          <Box onClick={() => setSaveError(null)} sx={{ cursor: 'pointer', color: '#f87171', fontSize: 16, lineHeight: 1, px: '4px' }}>×</Box>
        </Box>
      )}

      {/* ── Main ── */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* Canvas */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#0a0c10', position: 'relative', overflow: 'hidden' }}>
          {/* Checkerboard */}
          <Box sx={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(45deg,#13151a 25%,transparent 25%),linear-gradient(-45deg,#13151a 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#13151a 75%),linear-gradient(-45deg,transparent 75%,#13151a 75%)', backgroundSize: '20px 20px', backgroundPosition: '0 0,0 10px,10px -10px,-10px 0', opacity: .5 }} />

          {/* Image */}
          <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
            {showOriginal ? (
              <Box sx={{ display: 'flex', gap: '16px', alignItems: 'center', maxWidth: '90%', maxHeight: '80%' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography sx={{ fontSize: 10, color: 'var(--text-disabled)', textTransform: 'uppercase', letterSpacing: '.08em', mb: '6px' }}>Original</Typography>
                  <Box component="img" src={imageUrl} alt="original" sx={{ maxWidth: 340, maxHeight: 260, borderRadius: '6px', boxShadow: '0 12px 40px rgba(0,0,0,.6)', display: 'block' }} />
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography sx={{ fontSize: 10, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '.08em', mb: '6px' }}>Preview</Typography>
                  <Box component="img" src={imageUrl} alt="preview" sx={{ maxWidth: 340, maxHeight: 260, borderRadius: '6px', boxShadow: '0 12px 40px rgba(0,0,0,.6)', display: 'block', filter: previewFilter, transform: previewTransform }} />
                </Box>
              </Box>
            ) : (
              <Box component="img"
                src={imageUrl}
                alt={file.original_name}
                sx={{ maxWidth: `${Math.min(90, 90 * zoom)}%`, maxHeight: `${Math.min(80, 80 * zoom)}%`, borderRadius: '4px', boxShadow: '0 24px 80px rgba(0,0,0,.7)', display: 'block', filter: previewFilter !== 'none' ? previewFilter : undefined, transform: previewTransform !== '' ? previewTransform : undefined, transition: 'transform .2s, filter .15s' }}
              />
            )}
          </Box>

          {/* Canvas toolbar */}
          <Box sx={{ position: 'absolute', bottom: '16px', left: '50%', transform: 'translateX(-50%)', zIndex: 5, display: 'flex', gap: '4px', alignItems: 'center', background: 'rgba(13,15,20,.88)', backdropFilter: 'blur(16px)', border: '1px solid var(--glass-border)', borderRadius: '40px', px: '8px', py: '5px' }}>
            {[
              { icon: '−', title: 'Zoom out', onClick: () => setZoom(z => Math.max(0.25, z - 0.25)) },
              { icon: null, title: 'zoom' },
              { icon: '+', title: 'Zoom in', onClick: () => setZoom(z => Math.min(3, z + 0.25)) },
              { icon: '⊡', title: 'Fit', onClick: () => setZoom(1) },
            ].map((b, i) =>
              b.icon === null ? (
                <Typography key={i} sx={{ fontSize: 11, color: 'var(--text-disabled)', px: '6px', fontVariantNumeric: 'tabular-nums', minWidth: 36, textAlign: 'center' }}>
                  {Math.round(zoom * 100)}%
                </Typography>
              ) : (
                <Box key={i} onClick={b.onClick} sx={{ width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--text-disabled)', fontSize: b.icon === '⊡' ? 12 : 17, '&:hover': { background: 'rgba(255,255,255,0.1)', color: 'var(--text-primary)' } }}>
                  {b.icon}
                </Box>
              )
            )}
          </Box>
        </Box>

        {/* Right Panel */}
        <Box sx={{ width: 300, flexShrink: 0, background: 'rgba(255,255,255,0.025)', borderLeft: '1px solid var(--glass-border)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

          {/* File info */}
          <Box sx={{ p: '10px 14px', borderBottom: '1px solid var(--glass-border)', display: 'flex', gap: '10px', alignItems: 'center', background: 'rgba(255,255,255,0.03)', flexShrink: 0 }}>
            <Box sx={{ width: 42, height: 42, borderRadius: '8px', overflow: 'hidden', border: '1px solid var(--glass-border)', flexShrink: 0 }}>
              <Box component="img" src={imageUrl} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.original_name}</Typography>
              <Box sx={{ display: 'flex', gap: '6px', mt: '2px', alignItems: 'center' }}>
                <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)' }}>{file.width && file.height ? `${file.width}×${file.height}` : '—'}</Typography>
                <Box sx={{ fontSize: 10, fontWeight: 700, color: 'var(--text-secondary)', background: 'rgba(255,255,255,0.05)', border: '1px solid var(--glass-border)', px: '5px', py: '1px', borderRadius: '4px', letterSpacing: '.03em' }}>{ext}</Box>
                <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)' }}>{fmtSize(file.size_bytes)}</Typography>
              </Box>
            </Box>
          </Box>

          {/* Output estimate */}
          {estSize && (
            <Box sx={{ p: '7px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(91,141,238,.06)', borderBottom: '1px solid rgba(91,141,238,.15)', flexShrink: 0 }}>
              <Box>
                <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)' }}>Estimated output</Typography>
                <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)', mt: '1px' }}>
                  {resizeEnabled ? `${resizeW} × ${resizeH}` : `${file.width ?? '?'} × ${file.height ?? '?'}`} · {format === 'original' ? ext : format.toUpperCase()}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)', fontVariantNumeric: 'tabular-nums' }}>~{fmtSize(estSize)}</Typography>
                {sizeDiff > 0 && <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#4ade80' }}>−{sizeDiff}% smaller</Typography>}
                {sizeDiff < 0 && <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#f87171' }}>+{Math.abs(sizeDiff)}% larger</Typography>}
              </Box>
            </Box>
          )}

          {/* Format chips */}
          <Box sx={{ p: '7px 14px', display: 'flex', alignItems: 'center', gap: '6px', borderBottom: '1px solid var(--glass-border)', flexShrink: 0 }}>
            <Typography sx={{ fontSize: 11, color: 'var(--text-disabled)', flexShrink: 0 }}>Output format</Typography>
            <Box sx={{ display: 'flex', gap: '4px', ml: 'auto' }}>
              {(['original', 'jpeg', 'webp', 'png', 'avif'] as const).map(f => (
                <Box key={f} onClick={() => setFormat(f)} sx={{ px: '7px', py: '2px', borderRadius: '5px', fontSize: 10, fontWeight: 700, cursor: 'pointer', border: '1px solid', letterSpacing: '.04em', textTransform: 'uppercase', transition: 'all .12s', borderColor: format === f ? 'var(--accent)' : 'var(--glass-border)', background: format === f ? 'var(--accent)' : 'rgba(255,255,255,0.04)', color: format === f ? '#fff' : 'var(--text-disabled)', '&:hover': format !== f ? { color: 'var(--text-primary)', borderColor: 'var(--text-disabled)' } : {} }}>
                  {f === 'original' ? 'Orig' : f === 'jpeg' ? 'JPG' : f.toUpperCase()}
                </Box>
              ))}
            </Box>
          </Box>

          {/* Scrollable sections */}
          <Box sx={{ flex: 1, overflowY: 'auto', '&::-webkit-scrollbar': { width: 4 }, '&::-webkit-scrollbar-thumb': { background: 'var(--glass-border)', borderRadius: 2 }, '&::-webkit-scrollbar-track': { background: 'transparent' } }}>

            {/* RESIZE */}
            <Box>
              <SectionHeader
                icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5b8dee" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><path d="M10 3h4M17 3v4M3 10v4M21 10v4M10 21h4M17 21v-4M3 17h4M21 17h-4"/></svg>}
                iconColor="rgba(91,141,238,.12)" title="Resize"
                badge={resizeEnabled ? `${resizeW} × ${resizeH}` : undefined}
                open={openSections.resize} onToggle={() => toggleSection('resize')}
              />
              {openSections.resize && (
                <Box sx={{ px: '14px', pb: '10px' }}>
                  <Toggle label="Enable resize" value={resizeEnabled} onChange={setResizeEnabled} />
                  {resizeEnabled && (
                    <>
                      <Box sx={{ display: 'flex', gap: '8px', alignItems: 'flex-end', mt: '8px' }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ fontSize: 10, color: 'var(--text-disabled)', textTransform: 'uppercase', letterSpacing: '.05em', mb: '3px' }}>Width</Typography>
                          <Box component="input" type="number" value={resizeW} onChange={e => handleResizeW(+e.target.value)} sx={{ width: '100%', background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', color: 'var(--text-primary)', fontSize: 13, fontFamily: 'inherit', p: '6px 9px', borderRadius: '8px', outline: 'none', '&:focus': { borderColor: 'var(--accent)' }, fontVariantNumeric: 'tabular-nums' }} />
                        </Box>
                        <Box onClick={() => setAspectLocked(p => !p)} sx={{ width: 28, height: 28, mb: '1px', border: '1px solid', borderColor: aspectLocked ? 'var(--accent)' : 'var(--glass-border)', background: aspectLocked ? 'rgba(91,141,238,.15)' : 'transparent', borderRadius: '7px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: aspectLocked ? 'var(--accent)' : 'var(--text-disabled)', fontSize: 14 }}>
                          {aspectLocked ? '🔗' : '✂'}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ fontSize: 10, color: 'var(--text-disabled)', textTransform: 'uppercase', letterSpacing: '.05em', mb: '3px' }}>Height</Typography>
                          <Box component="input" type="number" value={resizeH} onChange={e => handleResizeH(+e.target.value)} sx={{ width: '100%', background: 'var(--glass-bg)', border: '1px solid var(--glass-border)', color: 'var(--text-primary)', fontSize: 13, fontFamily: 'inherit', p: '6px 9px', borderRadius: '8px', outline: 'none', '&:focus': { borderColor: 'var(--accent)' }, fontVariantNumeric: 'tabular-nums' }} />
                        </Box>
                      </Box>
                      <Box sx={{ mt: '8px', display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                        {[['1080p', 1920, 1080], ['720p', 1280, 720], ['480p', 854, 480], ['4K', 3840, 2160]].map(([label, w, h]) => (
                          <Box key={label as string} onClick={() => { setResizeW(w as number); setResizeH(h as number); }} sx={{ px: '9px', py: '3px', borderRadius: '20px', fontSize: 11, fontWeight: 600, cursor: 'pointer', border: '1px solid var(--glass-border)', background: 'rgba(255,255,255,0.04)', color: 'var(--text-disabled)', '&:hover': { borderColor: 'var(--accent)', color: 'var(--accent)' } }}>
                            {label as string}
                          </Box>
                        ))}
                      </Box>
                      <Toggle label="Upscale if smaller" value={upscale} onChange={setUpscale} />
                    </>
                  )}
                </Box>
              )}
            </Box>

            {/* QUALITY */}
            <Box sx={{ borderTop: '1px solid var(--glass-border)' }}>
              <SectionHeader
                icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>}
                iconColor="rgba(74,222,128,.1)" title="Quality & Compression"
                open={openSections.quality} onToggle={() => toggleSection('quality')}
              />
              {openSections.quality && (
                <Box sx={{ px: '14px', pb: '10px' }}>
                  <SliderControl label="Quality" value={quality} min={1} max={100} displayValue={`${quality}%`} onChange={setQuality} />
                  <Box sx={{ height: 1, background: 'var(--glass-border)', my: '8px' }} />
                  <Toggle label="Strip EXIF metadata" value={stripExif} onChange={setStripExif} />
                  <Toggle label="Progressive JPEG" value={progressive} onChange={setProgressive} />
                </Box>
              )}
            </Box>

            {/* ROTATE */}
            <Box sx={{ borderTop: '1px solid var(--glass-border)' }}>
              <SectionHeader
                icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fbbf24" strokeWidth="2"><path d="M21.5 2v6h-6"/><path d="M2.5 22v-6h6"/><path d="M21.5 8A10 10 0 116.5 3.5l9 4.5"/></svg>}
                iconColor="rgba(251,191,36,.1)" title="Rotate & Flip"
                badge={rotateAngle !== 0 ? `${rotateAngle}°` : undefined}
                open={openSections.rotate} onToggle={() => toggleSection('rotate')}
              />
              {openSections.rotate && (
                <Box sx={{ px: '14px', pb: '10px' }}>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', mb: '10px' }}>
                    {[
                      { label: '90° CCW', icon: '↺', action: () => rotate90(-1) },
                      { label: '90° CW',  icon: '↻', action: () => rotate90(1) },
                      { label: 'Flip ↕',  icon: '↕', action: () => setFlip(p => !p), active: flip },
                      { label: 'Flip ↔',  icon: '↔', action: () => setFlop(p => !p), active: flop },
                    ].map(btn => (
                      <Box key={btn.label} onClick={btn.action} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px', py: '8px', borderRadius: '9px', cursor: 'pointer', border: '1px solid', transition: 'all .12s', borderColor: btn.active ? 'var(--accent)' : 'var(--glass-border)', background: btn.active ? 'rgba(91,141,238,.12)' : 'rgba(255,255,255,0.03)', color: btn.active ? 'var(--accent)' : 'var(--text-disabled)', '&:hover': { borderColor: 'var(--accent)', color: 'var(--accent)', background: 'rgba(91,141,238,.08)' } }}>
                        <Typography sx={{ fontSize: 20, lineHeight: 1 }}>{btn.icon}</Typography>
                        <Typography sx={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em' }}>{btn.label}</Typography>
                      </Box>
                    ))}
                  </Box>
                  <SliderControl label="Arbitrary angle" value={rotateAngle} min={-180} max={180} displayValue={`${rotateAngle}°`} onChange={setRotateAngle} />
                </Box>
              )}
            </Box>

            {/* COLOR */}
            <Box sx={{ borderTop: '1px solid var(--glass-border)' }}>
              <SectionHeader
                icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f97316" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M12 2v2M12 20v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M2 12h2M20 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42"/></svg>}
                iconColor="rgba(249,115,22,.1)" title="Color Adjustments"
                badge={[brightness, contrast, saturation, sharpness].some(v => v !== 0) ? 'Modified' : undefined}
                open={openSections.color} onToggle={() => toggleSection('color')}
              />
              {openSections.color && (
                <Box sx={{ px: '14px', pb: '10px' }}>
                  <SliderControl label="Brightness" value={brightness} min={-100} max={100} onChange={setBrightness} />
                  <SliderControl label="Contrast"   value={contrast}   min={-100} max={100} onChange={setContrast} />
                  <SliderControl label="Saturation" value={saturation} min={-100} max={100} onChange={setSaturation} />
                  <SliderControl label="Sharpness"  value={sharpness}  min={0} max={100} onChange={setSharpness} />
                  {[brightness, contrast, saturation, sharpness].some(v => v !== 0) && (
                    <Box onClick={() => { setBrightness(0); setContrast(0); setSaturation(0); setSharpness(0); }} sx={{ mt: '4px', fontSize: 11, color: 'var(--text-disabled)', cursor: 'pointer', textAlign: 'right', '&:hover': { color: 'var(--text-primary)' } }}>
                      Reset color adjustments
                    </Box>
                  )}
                </Box>
              )}
            </Box>

            {/* WATERMARK placeholder */}
            <Box sx={{ borderTop: '1px solid var(--glass-border)' }}>
              <SectionHeader
                icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ec4899" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>}
                iconColor="rgba(236,72,153,.1)" title="Watermark"
                open={openSections.watermark} onToggle={() => toggleSection('watermark')}
              />
              {openSections.watermark && (
                <Box sx={{ px: '14px', pb: '10px' }}>
                  <Typography sx={{ fontSize: 12, color: 'var(--text-disabled)' }}>Coming soon</Typography>
                </Box>
              )}
            </Box>

          </Box>

          {/* Footer — save / cancel */}
          <Box sx={{ p: '10px 12px', borderTop: '1px solid var(--glass-border)', display: 'flex', gap: '7px', background: 'rgba(255,255,255,0.02)', flexShrink: 0 }}>
            <Box
              onClick={() => handleClose()}
              sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', py: '7px', borderRadius: '8px', fontSize: 12, fontWeight: 500, cursor: 'pointer', color: 'var(--text-disabled)', border: '1px solid var(--glass-border)', background: 'transparent', '&:hover': { background: 'rgba(255,255,255,0.06)', color: 'var(--text-primary)' } }}
            >
              Cancel
            </Box>
            <Box
              onClick={() => !saving && save(true)}
              sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', py: '7px', borderRadius: '8px', fontSize: 12, fontWeight: 500, cursor: saving ? 'default' : 'pointer', color: 'var(--text-primary)', border: '1px solid var(--glass-border)', background: 'rgba(255,255,255,0.05)', opacity: saving ? .6 : 1, '&:hover': { background: 'rgba(255,255,255,0.1)' } }}
            >
              Save copy
            </Box>
            <Box
              onClick={() => !saving && save(false)}
              sx={{ flex: 1.4, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', py: '7px', borderRadius: '8px', fontSize: 12, fontWeight: 600, cursor: saving ? 'default' : 'pointer', color: '#fff', background: '#3a9e6e', opacity: saving ? .6 : 1, '&:hover': { background: '#34896a' } }}
            >
              {saving
                ? <CircularProgress size={11} sx={{ color: '#fff' }} />
                : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
              }
              Apply & replace
            </Box>
          </Box>

          {/* Status bar */}
          <Box sx={{ p: '5px 14px', borderTop: '1px solid var(--glass-border)', display: 'flex', gap: '14px', background: 'rgba(0,0,0,.2)', flexShrink: 0 }}>
            {[
              ['Original', file.width && file.height ? `${file.width} × ${file.height}` : '—'],
              ['Output', resizeEnabled ? `${resizeW} × ${resizeH}` : (file.width && file.height ? `${file.width} × ${file.height}` : '—')],
            ].map(([label, val]) => (
              <Typography key={label} sx={{ fontSize: 10, color: 'var(--text-disabled)', fontVariantNumeric: 'tabular-nums' }}>
                {label}: <Box component="span" sx={{ color: 'var(--text-secondary)', fontWeight: 500 }}>{val}</Box>
              </Typography>
            ))}
          </Box>

        </Box>
      </Box>
    </Box>
  );
};

export default ImageEditorPage;
